'use client';

// Legacy: pro-frontend/src/app/manage-portfolio/newpreview-portfolio/templates/
//   minimal-template/minimal-template.component.{ts,html,scss}
// Bespoke one-page hero + marquee + section-per-category layout — NOT the
// shared sidebar shell used by classic. Cursor-follower / nav-drawer gimmicks
// from the legacy template are intentionally omitted (no layout impact).

import { useState } from 'react';

import './minimal.css';

import type { PortfolioCategory, PortfolioCategoryEntry } from '../../types';
import {
  formatEntryDate,
  getEntryField,
  getEntryImages,
  getEntryLinks,
  hasAnyLangSkills,
  hasLanguageSkills,
  splitTitle,
} from './entry-helpers';
import { nl2brSanitize } from './sanitize-html';
import { SocialLinksNav } from './social-links';
import type { PortfolioTemplateProps } from './types';
import { getYoutubeEmbedUrl } from './youtube-embed';

function entryTitle(entry: PortfolioCategoryEntry): string {
  return String(
    entry.display_title || entry.title || getEntryField(entry, 'education_title') || 'Untitled',
  );
}

function isLanguageCategory(cat: PortfolioCategory): boolean {
  return cat.category_type === 'language' || (cat.title || '').toLowerCase().includes('language');
}

const KNOWN_TYPES = new Set(['skill', 'work', 'experience', 'education', 'academic_project', 'language']);

export function MinimalTemplate({ data, isPublicView, theme, primaryColor, editHref }: PortfolioTemplateProps) {
  const [selectedPreviewImage, setSelectedPreviewImage] = useState<string | null>(null);

  const categories = data.categories || [];
  const skillCategory = categories.find((c) => c.category_type === 'skill' && c.entries && c.entries.length > 0);

  function renderGallery(entry: PortfolioCategoryEntry) {
    const images = getEntryImages(entry);
    if (images.length === 0) return null;
    return (
      <div className="entry-gallery">
        {images.map((img, i) => (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            key={String(img.id ?? i)}
            src={img.file_url}
            alt={img.file_title || ''}
            className="gallery-item"
            onClick={() => setSelectedPreviewImage(String(img.file_url || ''))}
          />
        ))}
      </div>
    );
  }

  function renderLinks(entry: PortfolioCategoryEntry) {
    const links = getEntryLinks(entry);
    if (links.length === 0) return null;
    return (
      <div className="entry-links">
        {links.map((link, li) => {
          const embedUrl = getYoutubeEmbedUrl(link.link);
          if (embedUrl) {
            return (
              <div className="video-embed-wrap" key={String(link.id ?? li)}>
                <iframe src={embedUrl} allowFullScreen title="Embedded video" />
              </div>
            );
          }
          if (!link.link) return null;
          return (
            <a href={link.link} target="_blank" rel="noopener noreferrer" className="link-card" key={String(link.id ?? li)}>
              <div className="link-info">
                <div className="link-title">{link.link_meta_title || link.link}</div>
                {link.link_meta_description ? <div className="link-desc">{link.link_meta_description}</div> : null}
                <div className="link-url" style={{ color: primaryColor }}>
                  {link.link}
                </div>
              </div>
            </a>
          );
        })}
      </div>
    );
  }

  function renderSectionHeader(title: string | undefined) {
    const parts = splitTitle(title);
    return (
      <div className="section-header">
        <h2 className="section-title">
          {parts.first}
          <br />
          <span className="section-title-secondary" style={{ color: primaryColor }}>
            {parts.rest}
          </span>
        </h2>
      </div>
    );
  }

  return (
    <div
      className={`template-minimal ${theme}`}
      style={{ ['--accent' as string]: primaryColor } as React.CSSProperties}
    >
      <nav>
        <span className="nav-logo">{data.name || 'Portfolio'}.</span>
      </nav>

      <section className="hero">
        <div className="hero-left">
          <h1 className="hero-name">{data.name}</h1>
          {data.designation ? <p className="hero-subtitle">{data.designation}</p> : null}

          <div className="hero-meta">
            {data.summary ? (
              <div>
                <div className="hero-col-label" style={{ color: primaryColor }}>
                  Profile
                </div>
                <div className="hero-col-body">{data.summary}</div>
              </div>
            ) : null}
            {skillCategory ? (
              <div className="hero-col-services">
                <div className="hero-col-label" style={{ color: primaryColor }}>
                  Specialization
                </div>
                <div className="hero-col-body">
                  {(skillCategory.entries || []).map((skill, i, arr) => (
                    <span key={String(skill.id ?? i)}>
                      {entryTitle(skill)}
                      {i < arr.length - 1 ? ' • ' : ''}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          <div className="hero-social">
            <SocialLinksNav data={data} className="social-links" />
          </div>

          {!isPublicView && editHref ? (
            <a href={editHref} className="hero-edit-link">
              Edit Portfolio
            </a>
          ) : null}
        </div>

        <div className="hero-right">
          {data.profile_image ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img className="hero-img" src={data.profile_image} alt={data.name} />
          ) : null}
        </div>
      </section>

      {skillCategory ? (
        <div className="marquee-wrap">
          <div className="marquee-track">
            {[0, 1].map((rep) =>
              (skillCategory.entries || []).map((skill, i) => (
                <div className="marquee-item" key={`${rep}-${String(skill.id ?? i)}`}>
                  <span>{entryTitle(skill)}</span>
                  <span className="marquee-dot" style={{ backgroundColor: primaryColor }} />
                </div>
              )),
            )}
          </div>
        </div>
      ) : null}

      {categories.map((cat, ci) => {
        if (!cat.entries || cat.entries.length === 0) return null;

        if (isLanguageCategory(cat)) {
          const nameOnly = cat.language_display_mode === 'name_only' || !hasAnyLangSkills(cat.entries);
          return (
            <div className="lang-section section" key={String(cat.id ?? ci)}>
              {renderSectionHeader(cat.title)}
              <div className={`lang-grid${nameOnly ? ' name-only-grid' : ''}`}>
                {cat.entries.map((lang, i) => (
                  <div
                    className={`lang-card${nameOnly || !hasLanguageSkills(lang) ? ' name-only-card' : ''}`}
                    key={String(lang.id ?? i)}
                  >
                    <div className="lang-name">{lang.title}</div>
                    {!nameOnly && hasLanguageSkills(lang) ? (
                      <div className="lang-skills">
                        {getEntryField(lang, 'listening_level') ? (
                          <span>
                            <strong>Listening:</strong> {getEntryField(lang, 'listening_level')}
                          </span>
                        ) : null}
                        {getEntryField(lang, 'reading_level') ? (
                          <span>
                            <strong>Reading:</strong> {getEntryField(lang, 'reading_level')}
                          </span>
                        ) : null}
                        {getEntryField(lang, 'spoken_interaction_level') ? (
                          <span>
                            <strong>Spoken Interaction:</strong> {getEntryField(lang, 'spoken_interaction_level')}
                          </span>
                        ) : null}
                        {getEntryField(lang, 'writing_level') ? (
                          <span>
                            <strong>Writing:</strong> {getEntryField(lang, 'writing_level')}
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          );
        }

        if (cat.category_type === 'skill') {
          return (
            <div className="skills-section section" key={String(cat.id ?? ci)}>
              {renderSectionHeader(cat.title)}
              <div className="skills-grid">
                {cat.entries.map((skill, i) => (
                  <div className="skills-card" key={String(skill.id ?? i)}>
                    <div className="skills-title" style={{ color: primaryColor }}>
                      {entryTitle(skill)}
                    </div>
                    {skill.description ? (
                      <div
                        className="skills-list"
                        dangerouslySetInnerHTML={{ __html: nl2brSanitize(skill.description) }}
                      />
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          );
        }

        if (cat.category_type === 'work' || cat.category_type === 'experience' || cat.category_type === 'education') {
          const isEducation = cat.category_type === 'education';
          return (
            <div className="exp-section section" key={String(cat.id ?? ci)}>
              {renderSectionHeader(cat.title)}
              {cat.entries.map((entry, i) => {
                const dates = formatEntryDate(entry);
                const role = isEducation ? getEntryField(entry, 'education_title') : entry.title;
                const company = isEducation ? getEntryField(entry, 'education_institution_name') : entry.work_company;
                const spec = getEntryField(entry, 'education_specialization_title');
                const score = getEntryField(entry, 'education_score');
                const desc = entry.description || entry.display_description || '';
                return (
                  <div className="exp-row" key={String(entry.id ?? i)}>
                    {dates ? (
                      <div className="exp-year" style={{ color: primaryColor }}>
                        {dates}
                      </div>
                    ) : null}
                    <div className="exp-items">
                      <div className="exp-item">
                        <div className="exp-role" style={{ color: primaryColor }}>
                          {role || entryTitle(entry)}
                        </div>
                        {company ? <div className="exp-company">{company}</div> : null}
                        {spec ? (
                          <div className="exp-specialization" style={{ color: primaryColor }}>
                            {spec}
                          </div>
                        ) : null}
                        {score ? <div className="exp-score">{score}</div> : null}
                        {desc ? (
                          <div
                            className="exp-description"
                            dangerouslySetInnerHTML={{ __html: nl2brSanitize(desc) }}
                          />
                        ) : null}
                        {renderGallery(entry)}
                        {renderLinks(entry)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        }

        if (cat.category_type === 'academic_project') {
          return (
            <div className="proj-section section" key={String(cat.id ?? ci)}>
              {renderSectionHeader(cat.title)}
              <div className="proj-grid">
                {cat.entries.map((entry, i) => {
                  const images = getEntryImages(entry);
                  const firstImage = images[0];
                  return (
                    <div
                      className={`proj-card${firstImage ? '' : ' no-image'}`}
                      key={String(entry.id ?? i)}
                      onClick={() => firstImage && setSelectedPreviewImage(String(firstImage.file_url || ''))}
                    >
                      <div className="proj-img-wrap">
                        {firstImage ? (
                          // eslint-disable-next-line @next/next/no-img-element
                          <img src={firstImage.file_url} alt={entryTitle(entry)} />
                        ) : null}
                        <div className="proj-content">
                          <div className="proj-cat" style={{ color: primaryColor }}>
                            {cat.title}
                          </div>
                          <div className="proj-name">{entryTitle(entry)}</div>
                          {!firstImage && (entry.description || entry.display_description) ? (
                            <div
                              className="proj-desc"
                              dangerouslySetInnerHTML={{
                                __html: nl2brSanitize(entry.description || entry.display_description),
                              }}
                            />
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }

        if (!KNOWN_TYPES.has(cat.category_type || '')) {
          return (
            <div className="misc-section section" key={String(cat.id ?? ci)}>
              {renderSectionHeader(cat.title)}
              <div className="misc-grid">
                {cat.entries.map((item, i) => (
                  <div className="misc-card" key={String(item.id ?? i)}>
                    <h3 style={{ color: primaryColor }}>{entryTitle(item)}</h3>
                    {(item.description || item.display_description) ? (
                      <p
                        dangerouslySetInnerHTML={{
                          __html: nl2brSanitize(item.description || item.display_description),
                        }}
                      />
                    ) : null}
                    {renderGallery(item)}
                    {renderLinks(item)}
                  </div>
                ))}
              </div>
            </div>
          );
        }

        return null;
      })}

      <div className="cta-section">
        <div className="cta-inner">
          <div className="cta-text">
            <div className="cta-eyebrow" style={{ color: primaryColor }}>
              Get in touch
            </div>
            <h2 className="cta-title">
              Connect
              <br />
              <em style={{ color: primaryColor }}>with {data.name}</em>
            </h2>
          </div>
          <div className="cta-deco">
            <div className="spin-ring">
              <div className="spin-core" style={{ backgroundColor: primaryColor }}>
                {(data.name || '?').charAt(0)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {selectedPreviewImage ? (
        <div className="preview-overlay" onClick={() => setSelectedPreviewImage(null)}>
          <div className="preview-content" onClick={(e) => e.stopPropagation()}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={selectedPreviewImage} alt="Preview" className="preview-img" />
            <div className="preview-close" onClick={() => setSelectedPreviewImage(null)}>
              <i className="fa-solid fa-xmark" aria-hidden="true" />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
